/**
 * The warm-up and the health gate of an Outreach mailbox (spec 2026-10-11 §8.2, §8.3; owner request
 * 2026-10-21). A customer sends pitches from an own mailbox. A new mailbox sends a few pitches a day
 * and its cap goes up step by step over 21 days: this ramp is the warm-up. Pure.
 */

/** From day `fromDay` after the connect, the mailbox may send `perDay` pitches a day. */
export const OUTREACH_RAMP: Array<{ fromDay: number; perDay: number }> = [
  { fromDay: 0, perDay: 3 },
  { fromDay: 3, perDay: 5 },
  { fromDay: 7, perDay: 10 },
  { fromDay: 10, perDay: 20 },
  { fromDay: 14, perDay: 30 },
  { fromDay: 21, perDay: 50 },
];

/** The warm-up ends on this day after the connect. From then on the mailbox has the full cap. */
export const OUTREACH_WARMUP_DAYS = 21;

/** A seasoned mailbox (old account with sent mail) starts at this cap. */
export const OUTREACH_SEASONED_START_CAP = 10;
/** After a resume, the cap is at most this value for this many days. */
export const OUTREACH_RESUME_DAYS = 7;
export const OUTREACH_RESUME_CAP = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The input of the cap rules: the same for `mailboxDailyCap` and `mailboxWarmup`. */
export interface MailboxCapInput {
  connectedAt: Date;
  now: Date;
  seasoned: boolean;
  resumedAt: Date | null;
  providerCap: number;
}

/** The warm-up facts of one mailbox at `now`. */
export interface MailboxWarmup {
  /** True while the mailbox is in the warm-up (`day` is less than `totalDays`). */
  warming: boolean;
  /** The whole days since the connect (0 on the connect day). It can be more than `totalDays`. */
  day: number;
  totalDays: number;
  /** The pitches that the mailbox may send today. It is the value of `mailboxDailyCap`. */
  todayCap: number;
  /** The next cap that is different from `todayCap`, or null when the cap does not change again. */
  nextCap: number | null;
  /** The time when `nextCap` starts, or null. */
  nextCapAt: Date | null;
}

function rampCap(day: number): number {
  let cap = OUTREACH_RAMP[0]?.perDay ?? 0;
  for (const step of OUTREACH_RAMP) {
    if (day >= step.fromDay) {
      cap = step.perDay;
    }
  }
  return cap;
}

function rampDayOf(connectedAt: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - connectedAt.getTime()) / DAY_MS));
}

/** The pitches that one mailbox may send today. */
export function mailboxDailyCap(input: MailboxCapInput): number {
  let cap = rampCap(rampDayOf(input.connectedAt, input.now));
  if (input.seasoned) {
    cap = Math.max(cap, OUTREACH_SEASONED_START_CAP);
  }
  if (input.resumedAt !== null) {
    const sinceResume = input.now.getTime() - input.resumedAt.getTime();
    if (sinceResume >= 0 && sinceResume < OUTREACH_RESUME_DAYS * DAY_MS) {
      cap = Math.min(cap, OUTREACH_RESUME_CAP);
    }
  }
  return Math.max(0, Math.min(cap, input.providerCap));
}

/**
 * The warm-up facts of one mailbox. `todayCap` is always the value of `mailboxDailyCap`. The next
 * cap is the first change of the cap after `now`: a ramp step or the end of a resume window.
 */
export function mailboxWarmup(input: MailboxCapInput): MailboxWarmup {
  const day = rampDayOf(input.connectedAt, input.now);
  const todayCap = mailboxDailyCap(input);
  const changeTimes = OUTREACH_RAMP.map((step) => input.connectedAt.getTime() + step.fromDay * DAY_MS);
  if (input.resumedAt !== null) {
    changeTimes.push(input.resumedAt.getTime() + OUTREACH_RESUME_DAYS * DAY_MS);
  }
  const later = changeTimes.filter((time) => time > input.now.getTime()).sort((a, b) => a - b);
  let nextCap: number | null = null;
  let nextCapAt: Date | null = null;
  for (const time of later) {
    const at = new Date(time);
    const cap = mailboxDailyCap({ ...input, now: at });
    if (cap !== todayCap) {
      nextCap = cap;
      nextCapAt = at;
      break;
    }
  }
  return {
    warming: day < OUTREACH_WARMUP_DAYS,
    day,
    totalDays: OUTREACH_WARMUP_DAYS,
    todayCap,
    nextCap,
    nextCapAt,
  };
}

/** The least sends before the gate can pause a mailbox. */
export const OUTREACH_HEALTH_MIN_SENDS = 20;
export const OUTREACH_HEALTH_MAX_BOUNCE_RATE = 0.03;
export const OUTREACH_HEALTH_MAX_COMPLAINT_RATE = 0.001;
export const OUTREACH_HEALTH_MAX_UNSUBSCRIBE_RATE = 0.05;

/**
 * `pause` when the mailbox sent 20 or more pitches and its hard bounces are over 3 %, its
 * complaints over 0.1 % or its "unsubscribe" replies over 5 % (over the last 100 sends or 7 days).
 */
export function mailboxHealthGate(stats: {
  sends: number;
  hardBounces: number;
  complaints: number;
  unsubscribeReplies: number;
}): 'ok' | 'pause' {
  if (stats.sends < OUTREACH_HEALTH_MIN_SENDS) {
    return 'ok';
  }
  const over =
    stats.hardBounces / stats.sends > OUTREACH_HEALTH_MAX_BOUNCE_RATE ||
    stats.complaints / stats.sends > OUTREACH_HEALTH_MAX_COMPLAINT_RATE ||
    stats.unsubscribeReplies / stats.sends > OUTREACH_HEALTH_MAX_UNSUBSCRIBE_RATE;
  return over ? 'pause' : 'ok';
}
